import React, { useMemo } from 'react';
import { Modal, Text, Tabs, Button, Divider, Grid, Row, Col } from 'rsuite';
import dayjs from 'dayjs';
import IconIndicator from '../user-feedback/IconIndicator';
import { TAX_PAYMENT_PERIOD_TYPES_ENUM } from '../../common/enums';
import ResponsiveModal from '../ResponsiveModal';
import { BREAKPOINT_ENUM } from '../../common/enums';
import BasicStyledCheckbox from '../form/BasicStyledCheckbox';
import { ExtendedPaymentPeriod } from 'src/types/tax';
import { useResize } from 'src/hooks/useResize';

interface AddAdditionalPeriodsModalProps {
  availableReportingPeriods: Array<ExtendedPaymentPeriod>;
  selectedPeriods: Array<ExtendedPaymentPeriod>;
  onSelect: (period: ExtendedPaymentPeriod) => void;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  taxPaymentPeriod: TAX_PAYMENT_PERIOD_TYPES_ENUM;
}

interface SelectedPeriods extends ExtendedPaymentPeriod {
  selected: boolean;
}

const AddAdditionalPeriodsModal = ({
  availableReportingPeriods,
  isOpen,
  onSelect,
  selectedPeriods,
  setOpen,
  taxPaymentPeriod,
}: AddAdditionalPeriodsModalProps) => {
  const resizeTrigger = useResize();
  const closeModal = () => setOpen(false);

  const hasData = useMemo(() => {
    return !!availableReportingPeriods && availableReportingPeriods.length > 0;
  }, [availableReportingPeriods]);

  const years = useMemo(
    () =>
      Array.from(new Set(availableReportingPeriods?.map((item) => item.year))),
    [availableReportingPeriods]
  );

  const isMobile = useMemo(
    () => window.innerWidth < BREAKPOINT_ENUM.SMALL,
    [resizeTrigger]
  );

  const gridTemplateColumn = useMemo(() => {
    if (
      taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY ||
      taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.NY_FISCAL
    ) {
      return 'repeat(1, 1fr)';
    }
    return isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)';
  }, [taxPaymentPeriod, isMobile]);

  const periodsPerYear = useMemo((): Record<string, Array<SelectedPeriods>> => {
    return [...availableReportingPeriods]
      ?.sort((a, b) =>
        dayjs(b?.startDate).isBefore(dayjs(a?.startDate)) ? 1 : -1
      )
      .reduce(
        (
          acc: Record<string, Array<SelectedPeriods>>,
          period: ExtendedPaymentPeriod
        ) => {
          if (!acc[period.year]) {
            acc[period.year] = [];
          }
          acc[period.year].push({
            ...period,
            selected: selectedPeriods.some(
              (p) => p.startDate === period.startDate
            ),
          });
          return acc;
        },
        {}
      );
  }, [availableReportingPeriods, selectedPeriods]);

  const gridTemplateRows = useMemo(
    () => (year: number) => {
      const periodsInYear = periodsPerYear[year]?.length || 0;

      if (
        taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.QUARTERLY ||
        taxPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.NY_FISCAL
      ) {
        return `repeat(${Math.ceil(periodsInYear)}, 1fr)`;
      }
      return isMobile
        ? `repeat(${Math.ceil(periodsInYear / 2)}, 1fr)`
        : 'repeat(4, 1fr)';
    },
    [taxPaymentPeriod, isMobile, periodsPerYear]
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={() => closeModal()}
    >
      <Modal.Header>
        <Modal.Title
          as="h2"
          className="text-bold no-padding-modal-section"
        >
          Report Additional Tax
        </Modal.Title>
        <Divider />
      </Modal.Header>
      {hasData ? (
        <>
          <Modal.Body>
            <Grid
              fluid
              className="no-padding-modal-section"
            >
              <Row className="mb-12">
                <Col xs={24}>
                  <Text className="mb-8">
                    Select the periods for which you wish to report tax,
                    including reporting $0.00 receipts.
                  </Text>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <Tabs
                    defaultActiveKey={years[0].toString()}
                    appearance="subtle"
                  >
                    {years.map((year: number) => (
                      <Tabs.Tab
                        key={`year_tab_${year}`}
                        eventKey={year.toString()}
                        title={year}
                      >
                        <div
                          style={{
                            display: 'inline-grid',
                            gridAutoFlow: 'column',
                            gridTemplateColumns: gridTemplateColumn,
                            gridTemplateRows: gridTemplateRows(year),
                            rowGap: '15px',
                            columnGap: '60px',
                            marginTop: '8px',
                          }}
                        >
                          {periodsPerYear[year]?.map((period) => (
                            <BasicStyledCheckbox
                              key={`period_${period.id}`}
                              onChange={() => {
                                onSelect(period);
                              }}
                              checked={period.selected}
                              label={period.name}
                            />
                          ))}
                        </div>
                      </Tabs.Tab>
                    ))}
                  </Tabs>
                </Col>
              </Row>
            </Grid>
          </Modal.Body>
          <Modal.Footer>
            <Divider />
            <Grid
              fluid
              className="no-padding-modal-section"
            >
              <Row>
                <Col xs={24}>
                  <Button
                    type="button"
                    className="btn-primary"
                    onClick={() => closeModal()}
                  >
                    Add Periods
                  </Button>
                  <Button
                    onClick={() => closeModal()}
                    appearance="subtle"
                    className="btn-link-underline-hover"
                    style={{ padding: '8px 12px' }}
                  >
                    Close
                  </Button>
                </Col>
              </Row>
            </Grid>
          </Modal.Footer>
        </>
      ) : (
        <Modal.Body
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconIndicator />
        </Modal.Body>
      )}
    </ResponsiveModal>
  );
};

export default AddAdditionalPeriodsModal;
